import { useState } from 'react';
import type { AdminTransformation } from '@st/contract';
import {
  useObjaviTransformaciju,
  useObrisiTransformaciju,
  usePosaljiFotografiju,
  useSpremiTransformaciju,
  useTransformacije,
} from '../../api/queries';
import { PageHeader } from '../../components/PageHeader';
import { QueryBoundary } from '../../components/QueryBoundary';
import { useTranslation } from '../../i18n/I18nProvider';
import { toMutationMessage } from '../../i18n/errorMessage';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  NumberInput,
  Select,
  SkeletonList,
  Textarea,
  useToast,
} from '../../ui';

/** 8 MB po fotografiji — `/v1/files` čita tijelo u memoriju, a Worker ima 128 MB. */
const MAX_BYTES = 8 * 1024 * 1024;
const TIPOVI = ['image/jpeg', 'image/png', 'image/webp'];

interface Obrazac {
  authorName: string;
  authorAge: number | '';
  jedinica: 'kg' | 'cm';
  vrijednost: number | '';
  textHr: string;
  textEn: string;
}

const PRAZAN: Obrazac = {
  authorName: '',
  authorAge: '',
  jedinica: 'kg',
  vrijednost: '',
  textHr: '',
  textEn: '',
};

function uObrazac(t: AdminTransformation): Obrazac {
  return {
    authorName: t.authorName,
    authorAge: t.authorAge ?? '',
    jedinica: t.metric?.unit ?? 'kg',
    vrijednost: t.metric ? t.metric.value : '',
    textHr: t.textHr ?? '',
    textEn: t.textEn ?? '',
  };
}

/**
 * Unos transformacija „prije / poslije" za javni sajt.
 *
 * Zašto postoji: tablica `testimonials` i javno čitanje postoje od početka, ali
 * ničim se nije moglo pisati — sajt je mogao prikazati samo ono što bi netko
 * ručno ubacio u bazu. Ovo je taj kraj.
 *
 * Objava je zasebna radnja, ne polje u obrascu. Fotografija tuđeg tijela ne
 * izlazi na javni sajt kao nuspojava klika na „Spremi".
 */
export function TrainerTransformations() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const query = useTransformacije();
  const spremi = useSpremiTransformaciju();
  const objavi = useObjaviTransformaciju();
  const obrisi = useObrisiTransformaciju();
  const posalji = usePosaljiFotografiju();

  const [uredjujem, setUredjujem] = useState<string | null>(null);
  const [novi, setNovi] = useState(false);
  const [obrazac, setObrazac] = useState<Obrazac>(PRAZAN);
  const [greska, setGreska] = useState<string | null>(null);
  const [zaBrisanje, setZaBrisanje] = useState<AdminTransformation | null>(null);

  function otvoriNovi() {
    setNovi(true);
    setUredjujem(null);
    setObrazac(PRAZAN);
    setGreska(null);
  }

  function otvoriUredjivanje(red: AdminTransformation) {
    setUredjujem(red.id);
    setNovi(false);
    setObrazac(uObrazac(red));
    setGreska(null);
  }

  function zatvori() {
    setNovi(false);
    setUredjujem(null);
    setGreska(null);
  }

  async function posaljiObrazac(id?: string) {
    setGreska(null);
    const broj = obrazac.vrijednost === '' ? null : obrazac.vrijednost;
    try {
      await spremi.mutateAsync({
        ...(id ? { id } : {}),
        input: {
          authorName: obrazac.authorName.trim(),
          authorAge: obrazac.authorAge === '' ? null : obrazac.authorAge,
          textHr: obrazac.textHr.trim() === '' ? null : obrazac.textHr.trim(),
          textEn: obrazac.textEn.trim() === '' ? null : obrazac.textEn.trim(),
          metric:
            broj === null || !Number.isFinite(broj) || broj === 0
              ? null
              : { unit: obrazac.jedinica, value: Math.trunc(broj) },
        },
      });
      toast(t('transformacije.spremljeno'), 'success');
      zatvori();
    } catch (e) {
      setGreska(toMutationMessage(t, e));
    }
  }

  async function odaberiFotografiju(
    id: string,
    faza: 'prije' | 'poslije',
    file: File | undefined,
  ) {
    if (!file) return;
    setGreska(null);
    if (!TIPOVI.includes(file.type)) {
      setGreska(t('transformacije.krivTip'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setGreska(t('transformacije.prevelika', { max: String(MAX_BYTES / 1024 / 1024) }));
      return;
    }
    try {
      await posalji.mutateAsync({ id, faza, file });
      toast(t('transformacije.spremljeno'), 'success');
    } catch (e) {
      setGreska(toMutationMessage(t, e));
    }
  }

  async function prebaciObjavu(red: AdminTransformation) {
    setGreska(null);
    try {
      await objavi.mutateAsync({ id: red.id, objavi: !red.published });
    } catch (e) {
      setGreska(toMutationMessage(t, e));
    }
  }

  const polja = (
    <div className="flex flex-col gap-4">
      <Field id="tr-ime" label={t('transformacije.ime')} hint={t('transformacije.imeHint')} required>
        <Input
          id="tr-ime"
          value={obrazac.authorName}
          maxLength={80}
          onChange={(e) => setObrazac({ ...obrazac, authorName: e.target.value })}
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-3">
        <Field id="tr-godine" label={t('transformacije.godine')}>
          <NumberInput
            id="tr-godine"
            value={obrazac.authorAge}
            min={14}
            max={100}
            onValueChange={(v) => setObrazac({ ...obrazac, authorAge: v })}
          />
        </Field>
        <Field id="tr-mjera" label={t('transformacije.mjera')} hint={t('transformacije.mjeraHint')}>
          <NumberInput
            id="tr-mjera"
            value={obrazac.vrijednost}
            min={-200}
            max={200}
            onValueChange={(v) => setObrazac({ ...obrazac, vrijednost: v })}
          />
        </Field>
        <Select
          id="tr-jedinica"
          label=" "
          value={obrazac.jedinica}
          options={[
            { value: 'kg', label: t('transformacije.jedinicaKg') },
            { value: 'cm', label: t('transformacije.jedinicaCm') },
          ]}
          onChange={(e) =>
            setObrazac({ ...obrazac, jedinica: e.target.value === 'cm' ? 'cm' : 'kg' })
          }
        />
      </div>

      <Field id="tr-hr" label={t('transformacije.tekstHr')}>
        <Textarea
          id="tr-hr"
          rows={3}
          maxLength={600}
          value={obrazac.textHr}
          onChange={(e) => setObrazac({ ...obrazac, textHr: e.target.value })}
        />
      </Field>
      <Field id="tr-en" label={t('transformacije.tekstEn')}>
        <Textarea
          id="tr-en"
          rows={3}
          maxLength={600}
          value={obrazac.textEn}
          onChange={(e) => setObrazac({ ...obrazac, textEn: e.target.value })}
        />
      </Field>
    </div>
  );

  return (
    <>
      <PageHeader
        title={t('transformacije.title')}
        action={
          !novi ? (
            <Button onClick={otvoriNovi}>{t('transformacije.nova')}</Button>
          ) : undefined
        }
      />

      <p className="max-w-prose text-[14.5px] text-ink-muted">{t('transformacije.intro')}</p>
      <p className="mt-2 max-w-prose text-[13px] text-ink-muted">{t('transformacije.consent')}</p>
      <p className="mt-2 max-w-prose text-[13px] text-ink-muted">{t('transformacije.uzKod')}</p>

      {greska && (
        <p role="alert" className="mt-4 text-[14px] text-danger">
          {greska}
        </p>
      )}

      {novi && (
        <Card className="mt-6 p-6">
          {polja}
          <div className="mt-5 flex gap-3">
            <Button onClick={() => void posaljiObrazac()} disabled={spremi.isPending}>
              {t('transformacije.spremi')}
            </Button>
            <Button variant="secondary" onClick={zatvori}>
              {t('common.cancel')}
            </Button>
          </div>
        </Card>
      )}

      <QueryBoundary query={query} skeleton={<SkeletonList rows={3} />}>
        {(data) =>
          data.items.length === 0 && !novi ? (
            <EmptyState message={t('transformacije.prazno')} />
          ) : (
            <ul className="mt-6 flex list-none flex-col gap-4 p-0">
              {data.items.map((red) => (
                <li key={red.id}>
                  <Card className="p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display text-[19px] font-semibold">
                          {red.authorName}
                          {red.authorAge !== null && (
                            <span className="text-ink-muted">, {red.authorAge}</span>
                          )}
                        </h3>
                        {red.metric && (
                          <p className="mt-1 text-[14px] text-ink-muted">
                            {red.metric.value > 0 ? '+' : '−'}
                            {Math.abs(red.metric.value)} {red.metric.unit}
                          </p>
                        )}
                      </div>
                      <Badge tone={red.published ? 'success' : 'neutral'}>
                        {red.published
                          ? t('transformacije.objavljeno')
                          : t('transformacije.skica')}
                      </Badge>
                    </div>

                    {/*
                      Fotografije su samo provjera „je li ovo ona prava" — nisu
                      pregled. U punoj širini kartice jedan zapis zauzme cijeli
                      ekran i popis se ne da prelistati.
                    */}
                    <div className="mt-4 flex flex-wrap gap-5">
                      {(['prije', 'poslije'] as const).map((faza) => {
                        const url = faza === 'prije' ? red.photoBeforeUrl : red.photoAfterUrl;
                        const ulazId = `tr-${red.id}-${faza}`;
                        return (
                          <div key={faza} className="w-[160px]">
                            <p className="text-[13px] text-ink-muted">
                              {t(`transformacije.${faza}`)}
                            </p>
                            {url ? (
                              <img
                                src={url}
                                alt={`${t(`transformacije.${faza}`)} — ${red.authorName}`}
                                className="mt-2 aspect-[4/5] w-full rounded-lg object-cover"
                              />
                            ) : (
                              <div className="mt-2 aspect-[4/5] w-full rounded-lg border border-dashed border-line" />
                            )}
                            <label htmlFor={ulazId} className="mt-2 inline-block">
                              <span className="btn-like cursor-pointer text-[13px] text-accent-dark underline">
                                {url
                                  ? t('transformacije.zamijeni')
                                  : t('transformacije.postavi')}
                              </span>
                            </label>
                            <input
                              id={ulazId}
                              type="file"
                              accept={TIPOVI.join(',')}
                              className="sr-only"
                              onChange={(e) => {
                                void odaberiFotografiju(red.id, faza, e.target.files?.[0]);
                                e.target.value = '';
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>

                    {posalji.isPending && (
                      <p className="mt-3 text-[13px] text-ink-muted">
                        {t('transformacije.saljem')}
                      </p>
                    )}

                    {uredjujem === red.id ? (
                      <div className="mt-5 border-t border-line-subtle pt-5">
                        {polja}
                        <div className="mt-5 flex gap-3">
                          <Button
                            onClick={() => void posaljiObrazac(red.id)}
                            disabled={spremi.isPending}
                          >
                            {t('transformacije.spremi')}
                          </Button>
                          <Button variant="secondary" onClick={zatvori}>
                            {t('common.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-5 flex flex-wrap gap-3">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => otvoriUredjivanje(red)}
                        >
                          {t('action.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant={red.published ? 'secondary' : 'primary'}
                          onClick={() => void prebaciObjavu(red)}
                          disabled={objavi.isPending}
                        >
                          {red.published
                            ? t('transformacije.sakrij')
                            : t('transformacije.objavi')}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setZaBrisanje(red)}
                        >
                          {t('transformacije.obrisi')}
                        </Button>
                      </div>
                    )}
                  </Card>
                </li>
              ))}
            </ul>
          )
        }
      </QueryBoundary>

      <ConfirmDialog
        open={zaBrisanje !== null}
        title={t('transformacije.obrisi')}
        question={t('transformacije.obrisiPotvrda')}
        confirmLabel={t('transformacije.obrisi')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        tone="danger"
        onClose={() => setZaBrisanje(null)}
        onConfirm={() => {
          const red = zaBrisanje;
          setZaBrisanje(null);
          if (red) void obrisi.mutateAsync(red.id).catch((e) => setGreska(toMutationMessage(t, e)));
        }}
      />
    </>
  );
}
